import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, Theme, createStyles, styled } from '@material-ui/core/styles';
import { TextField, FormControl, InputLabel, Select, Grid, Fab, CircularProgress, FabProps } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Slider, { SliderProps } from '@material-ui/core/Slider';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import PopperJs from 'popper.js';
import { useAsyncEffect } from 'use-async-effect';

import TagManager from 'react-gtm-module'

const setCookie = (cname: string, cvalue: string | number, exdays: number) => {
  var d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  var expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

const getParameterByName = (name: string, url: string = window.location.href) => {
  if (!url) url = window.location.href;
  name = name.replace(/[[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const getParameterValue = (paramName: string, url: string = window.location.href): string | null => {
  const urlParams = new URLSearchParams(new URL(url).search);
  const paramValue = urlParams.get(paramName);
  return paramValue ? paramValue.replace(/^#/, '') : null;
};

const getCookie = (cname: string): string => {
  var name = cname + "=";
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

interface ICodeToLanguageType {
  [index: string]: string;
}
const codeToLanguage: ICodeToLanguageType = {
  "af-ZA": "Afrikaans (South Africa)",
  "ar-XA": "Arabic",
  "bn-IN": "Bengali (India)",
  "bg-BG": "Bulgarian (Bulgaria)",
  "bg-bg": "Bulgarian (Bulgaria)",
  "ca-ES": "Catalan (Spain)",
  "ca-es": "Catalan (Spain)",
  "yue-HK": "Chinese (Hong Kong)",
  "cs-CZ": "Czech (Czech Republic)",
  "da-DK": "Danish (Denmark)",
  "nl-BE": "Dutch (Belgium)",
  "nl-NL": "Dutch (Netherlands)",
  "en-AU": "English (Australia)",
  "en-IN": "English (India)",
  "en-GB": "English (UK)",
  "en-US": "English (US)",
  "fil-PH": "Filipino (Philippines)",
  "fi-FI": "Finnish (Finland)",
  "fr-CA": "French (Canada)",
  "fr-FR": "French (France)",
  "de-DE": "German (Germany)",
  "el-GR": "Greek (Greece)",
  "gu-IN": "Gujarati (India)",
  "hi-IN": "Hindi (India)",
  "hu-HU": "Hungarian (Hungary)",
  "is-IS": "Icelandic (Iceland)",
  "is-is": "Icelandic (Iceland)",
  "id-ID": "Indonesian (Indonesia)",
  "it-IT": "Italian (Italy)",
  "ja-JP": "Japanese (Japan)",
  "kn-IN": "Kannada (India)",
  "ko-KR": "Korean (South Korea)",
  "lv-LV": "Latvian (Latvia)",
  "lv-lv": "Latvian (Latvia)",
  "ms-MY": "Malay (Malaysia)",
  "ml-IN": "Malayalam (India)",
  "cmn-CN": "Mandarin Chinese (China)",
  "cmn-TW": "Mandarin Chinese (Taiwan)",
  "nb-NO": "Norwegian (Norway)",
  "nb-no": "Norwegian (Norway)",
  "pl-PL": "Polish (Poland)",
  "pt-BR": "Portuguese (Brazil)",
  "pt-PT": "Portuguese (Portugal)",
  "pa-IN": "Punjabi (India)",
  "ro-RO": "Romanian (Romania)",
  "ru-RU": "Russian (Russia)",
  "sr-RS": "Serbian (Cyrillic)",
  "sr-rs": "Serbian (Cyrillic)",
  "sk-SK": "Slovak (Slovakia)",
  "es-ES": "Spanish (Spain)",
  "es-US": "Spanish (US)",
  "sv-SE": "Swedish (Sweden)",
  "ta-IN": "Tamil (India)",
  "te-IN": "Telugu (India)",
  "th-TH": "Thai (Thailand)",
  "tr-TR": "Turkish (Turkey)",
  "uk-UA": "Ukrainian (Ukraine)",
  "vi-VN": "Vietnamese (Vietnam)"
}

interface IGoogleVoice {
  languageCodes: string[];
  name: string;
  ssmlGender: 'MALE' | 'FEMALE';
  naturalSampleRateHertz: number;
}
interface IVoice {
  languageCode: string;
  language: string;
  name: string;
  gender: 'MALE' | 'FEMALE';
  persona: string;
}

let baseApiUrl = "https://texttospeechapi.wideo.co";
const baseApiUrlQueryParam = getParameterByName("baseApiUrl", window.location.href);
if (baseApiUrlQueryParam) {
  baseApiUrl = baseApiUrlQueryParam;
}

const tagManagerArgs = {
  gtmId: 'GTM-NV67WG6'
}

TagManager.initialize(tagManagerArgs)
// Custom Material UI styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: '95%',
      padding: '0 25px',
      margin: '0 25px',
      textAlign: 'center'
    },
    margin: {
      height: theme.spacing(3),
    },
    container: {
      display: 'flex',
      flexWrap: 'wrap'
    },
    component: {
      marginBottom: 20
    },
    componentError: {
      color: "#FF0000"
    },
    audioplayer: {
      width: '100%',
      marginBottom: '20px'
    },  
    textFieldLabel: {
      fontSize: '0.9rem'
    },
    fabRoot: {
      fontSize: '1.1rem',
      marginBottom: '20px',
      width: '100%'
    },
    fabProgress: {
      zIndex: 1,
      marginBottom: '10px',
    },
  }),
);

interface CustomSliderProps extends SliderProps {
  mainColor?: string;
}

const __useStyles = makeStyles<Theme, { mainColor?: string }>((theme) => ({
  colorPrimary: {
    color: (props) => `#${props.mainColor}` || theme.palette.primary.main,      
    '& .MuiSlider-thumbColorPrimary': {
      backgroundColor: (props) => `#${props.mainColor}` || theme.palette.primary.main,
    },
  },
}));

const CustomSlider: React.FC<CustomSliderProps> = ({ mainColor, ...props }) => {
  const classes = __useStyles({ mainColor });

  return <Slider {...props} classes={{ colorPrimary: classes.colorPrimary }} />;
};

interface CustomFabProps extends FabProps {
  mainColor: string;
  download?: boolean;
}

const _useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: (props: CustomFabProps) => `#${props.mainColor}`, // Agrega '#' antes de props.mainColor
      fontSize: '1.1rem',
      marginBottom: '20px',
      width: '100%',
      zIndex: 1,
      '&.MuiFab-root': {
        marginBottom: '10px',
      },
      '&:hover': {
        backgroundColor: (props: CustomFabProps) => `#${props.mainColor}`,
      },      
    },
  }),
)

const CustomFab: React.FC<CustomFabProps> = (props) => {
  const classes = _useStyles(props);

  return <Fab {...props} className={classes.root} />;
};


interface Props {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

// This function will show the values in tooltips for the sliders  
function ValueLabelComponent(props: Props) {
  const { children, open, value } = props;

  const popperRef = React.useRef<PopperJs | null>(null);
  React.useEffect(() => {
    if (popperRef.current) {
      popperRef.current.update();
    }
  });

  return (
    <Tooltip
      PopperProps={{
        popperRef,
      }}
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={value}
    >
      {children}
    </Tooltip>
  );
}


// Full-form component
export const TTSForm = () => {
  
  const audioElement = useRef<HTMLAudioElement>(null);

  const classes = useStyles();
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [speed, setSpeed] = useState<number | number[]>(1);
  const [voiceList, setVoiceList] = useState<IVoice[]>([]);
  const [voice, setVoice] = useState<string>('');
  const [usedCharacters, setUsedCharacters] = useState<number>(0);
  const [characterLimit, setCharacterLimit] = useState<number>(0);
  const [requestLimit, setRequestLimit] = useState<number>(0);
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [wideoSession, setWideoSession] = useState<string>('');

  const primaryColor = getParameterValue('primaryColor') || '#4285F4';
  const secondaryColor = getParameterValue('secondaryColor') || '#00c853'; 

  const postTextToSpeech = async (token: string = '') => {
    const response = await fetch(baseApiUrl + '/api/wideo-text-to-speech', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'recaptcha-token': token,
        'wideo-session-token': wideoSession,
        'used-characters': String(usedCharacters)
      },
      body: JSON.stringify({
        data: {
          text: text,
          speed: speed,
          voice: voice
        }
      })
    });

    const data = await response.json();
    if (data && data.result && data.result.url) {
      setUrl(data.result.url);
      setError(false);
      const updatedUsedCharacter = usedCharacters + text.length;
      setUsedCharacters(updatedUsedCharacter);      
      setCookie('TTSQty', updatedUsedCharacter, 1);
    } else {
      setUrl('');
      setError(true);
      setLoading(false);
    }

  }

  /**
   * Update loading state and load the new audio in player if the url state changes
   */
  useEffect(() => {
    if (audioElement.current && url) {
      audioElement.current.addEventListener('playing', (event) => {
        setLoading(false);
      });
      audioElement.current.load();
    } else {
      setLoading(false);
    }
  }, [url]);

  // Call to the TTS API
  const ConvertTextToSpeech = async (text: string, speed: number | number[]) => {

    setLoading(true);

    (window as any).grecaptcha.enterprise.ready(function() {
      (window as any).grecaptcha.enterprise.execute('6Lewf1AcAAAAAC-NTKBrvdaOiCEFhJcqvOH384BP', {action: wideoSession ? 'PaidTTS' : 'FreeTTS'}).then(function(token: string) {
          postTextToSpeech(token);
      });
    });
      
  }

  const onClickDownload = () => {
    (window as any).dataLayer.push({ 'event': 'download-mp3' });
  };

  const getConfig = async (token: string) => {

    // Call the voices list API mehod and then adds the result to the select list
    const response = await fetch(baseApiUrl + '/api/voices', {      
      method: 'GET',
      headers: {
        'wideo-session-token': getCookie("sessionID"),        
        'recaptcha-token': token,
      },
    });
    const jsonResponse = await response.json();  

    if (jsonResponse.result.msg === 'SUCCESS') {
      // Initialize character limits
      setCharacterLimit(jsonResponse.result.limit);
      setRequestLimit(jsonResponse.result.requestLimit);
      setWideoSession(jsonResponse.result.sessionId); // Session confirmed valid

      // Initialize voice list
      const apiVoices = jsonResponse.result.voices;

      const regexMatchVoiceName = /^([a-z]*-[A-Z|a-z|0-9]*)-(.*)-([A-Z]*)/;  

      const voices: IVoice[] = apiVoices.map((item: IGoogleVoice): IVoice => {  
        const result = regexMatchVoiceName.exec(item.name);
        const voiceType: string = result ? result[3] : ""; 
        let persona: string = "";
        switch (voiceType) {
          case "A": persona = item.ssmlGender === 'MALE' ? 'Tom Fraser' : 'Maria Henderson'; break;
          case "B": persona = item.ssmlGender === 'MALE' ? 'Jack Bailey' : 'Cheryl Lowe'; break;
          case "C": persona = item.ssmlGender === 'MALE' ? 'Peter Moore' : 'Alisha Howard'; break;
          case "D": persona = item.ssmlGender === 'MALE' ? 'Mike Stevens' : 'Amanda Rose'; break;
          case "E": persona = item.ssmlGender === 'MALE' ? 'Nick Ross' : 'Virginia Harper'; break;
          case "F": persona = item.ssmlGender === 'MALE' ? 'Harry Dawson' : 'Lisa Fisher'; break;
          case "G": persona = item.ssmlGender === 'MALE' ? 'Darnell Wallis' : 'Stacey Weston'; break;
          case "H": persona = item.ssmlGender === 'MALE' ? 'Johnathan Howard' : 'Elly Sparks'; break;
          case "I": persona = item.ssmlGender === 'MALE' ? 'Frazer Petersen' : 'Zarah Brady'; break;
          case "J": persona = item.ssmlGender === 'MALE' ? 'Trey Shepherd' : 'Nabilah Cairns'; break;
          default:
            break;
        };

        return {
          languageCode: item.languageCodes[0],
          language: codeToLanguage[item.languageCodes[0]],
          name: item.name,
          gender: item.ssmlGender,
          persona: persona
        }
      })

      const orderedVoices = voices.sort((a: IVoice, b: IVoice) => {
        if (a.language > b.language) {
          return 1;
        }

        if (a.language < b.language) {
          return -1;
        }

        return 0;
      });

      setVoiceList(orderedVoices);
      setVoice("en-US-Standard-C");
    } else {
      setError(true);
    }
  }

  useAsyncEffect(async () => {

    // Initialize the number of used characters
    const TTSQty = getCookie("TTSQty");
    if (TTSQty) {
      setUsedCharacters(parseInt(TTSQty));
    }

    (window as any).grecaptcha.enterprise.ready(function() {
      (window as any).grecaptcha.enterprise.execute('6Lewf1AcAAAAAC-NTKBrvdaOiCEFhJcqvOH384BP', {action: 'GetTTSConfig'}).then(function(token: string) {
        getConfig(token);
      });
    });

  }, []);

  let label: string = "Write your text here";
  let helperText = characterLimit ? "(Used " + (usedCharacters + text.length)  + " characters" : "";
  helperText += characterLimit ? " of " + characterLimit + " characters / day)" : "";
  const exceededLimit = characterLimit ? usedCharacters >= characterLimit : false;
  return (
    <Paper className={classes.root}>
      { exceededLimit && <Typography gutterBottom className={classes.componentError}>Sorry, you exceeded your daily quota of {characterLimit} characters.</Typography>
      }
      { error && <Typography gutterBottom className={classes.componentError}>Sorry, something went wrong! Please try again later or contact support.</Typography>
      }
      <TextField
        className={classes.component}
        fullWidth
        label={label}
        margin="normal"
        multiline={true}
        onChange={(e) => setText(e.target.value)}
        required
        rows={3}
        rowsMax={3}
        inputProps={{ maxLength: characterLimit ? characterLimit - usedCharacters : requestLimit }}
        InputLabelProps={{
          className: classes.textFieldLabel
        }}
        helperText={helperText}
      />

      <FormControl fullWidth className={classes.component}>
        <InputLabel>Voice</InputLabel>
        <Select
          value={voice}
          native={true}
          onChange={(e) => setVoice(e.currentTarget.value as string)}
          inputProps={{
            name: 'voice'
          }}
        >
          {            
            voiceList.map((item: IVoice, index: number) => {

              return <option
                key={item.name + "-" + index}
                value={item.name}
              >
                {item.language} - {item.persona}

              </option>

            })
          }
        </Select>
      </FormControl>

      <Typography gutterBottom>Speed</Typography>
      <CustomSlider
        ValueLabelComponent={ValueLabelComponent}
        aria-label="speed"
        className={classes.component}
        mainColor={primaryColor}   
        defaultValue={1}
        max={4}
        min={0.25}
        onChangeCommitted={(_, speed) => setSpeed(speed)}
        step={0.25}
      />

      <Grid container alignItems="center" spacing={3}>
        <Grid item sm={4} xs={6}>
          {!loading && <CustomFab
            className={classes.fabRoot}
            classes={{
              root: classes.fabRoot
            }}
            mainColor={secondaryColor}
            color="secondary"
            onClick={() => ConvertTextToSpeech(text, speed)}
            size="large"
            variant="extended"
            disabled={loading || !text || exceededLimit}
          >
            Preview
          </CustomFab>}
          {loading && <CircularProgress  className={classes.fabProgress} />} 
        </Grid>
        <Grid item sm={8} xs={6}>
          <audio
            autoPlay
            className={classes.audioplayer}
            controls
            controlsList="nodownload"
            id="audio-el"
            ref={audioElement}
          >
            <source id="audio-src" src={url} type="audio/mpeg"/>
          </audio>
        </Grid>
      </Grid>
      <CustomFab
        className={classes.component}
        classes={{
          root: classes.fabRoot
        }}
        mainColor={primaryColor}        
        color="primary"
        href={url}
        id="mp3-download"
        download
        disabled={(!url || loading ? true : false)}
        size="large"
        variant="extended"
        onClick={() => { onClickDownload() }}
      >
        Download MP3
      </CustomFab>

    </Paper>
  );
}